import { useState } from 'react';
import { useQuery } from '../../hooks/useQuery';
import {
  api, type PlanVersion, type PlanHeadcountRow, type ScheduleField, type ScheduleRow,
  type RampSpecInput, type RampPreview,
} from '../../lib/api';
import { Spinner, ActionButton, Modal } from '../ui';
import { SaveStatus, useSaveState } from '../SaveStatus';

/**
 * A seat's month-by-month trajectory: utilisation, productivity, bill rate,
 * hours and salary.
 *
 * WHY THIS SCREEN EXISTS. The seat editor holds one number per driver for the
 * whole horizon, and the only trajectory available there is a shared ramp curve
 * keyed on months-since-start — onboarding, and nothing else. The plan then
 * reads as though everyone is either fully productive or predictably ramping,
 * which is not true of anybody. Getting a person from 48% to 70% utilisation,
 * tapering someone out of a role, a stretch of leave and a phased return: all
 * of those are a path, and a path needs a row per month.
 *
 * The grid shows the EFFECTIVE value for every month, with the ones that come
 * from the seat's own scalar greyed out, so "where is this person actually
 * under-productive" is answerable by looking. Typing in a cell pins that month;
 * the ramp builder fills a window at once.
 */

const FIELDS: Array<{ key: ScheduleField; label: string; pct: boolean; hint: string }> = [
  { key: 'utilisation', label: 'Utilisation', pct: true, hint: 'Share of capacity that is billable work.' },
  { key: 'productivity', label: 'Productivity', pct: true, hint: 'Ramp factor on top of utilisation. A row here replaces the shared ramp curve for that month.' },
  { key: 'bill_rate', label: 'Bill rate', pct: false, hint: 'Charge-out rate per hour.' },
  { key: 'hours', label: 'Hours / month', pct: false, hint: 'Capacity before utilisation.' },
  { key: 'salary', label: 'Annual base', pct: false, hint: 'The annual base in effect that month. The loaded multiplier and merit events still apply on top.' },
];

const CURVES: Array<{ key: NonNullable<RampSpecInput['curve']>; label: string; hint: string }> = [
  { key: 'linear', label: 'Linear', hint: 'Even steps from the start value to the end value.' },
  { key: 's_curve', label: 'S-curve', hint: 'Slow, then fast, then slow — closer to how people actually recover or wind down.' },
  { key: 'hold', label: 'Hold flat', hint: 'One value for every month of the window. This is how leave and a plateau are expressed.' },
  { key: 'step_start', label: 'Step at the start', hint: 'Jumps straight to the end value in the first month.' },
  { key: 'step_end', label: 'Step at the end', hint: 'Holds the start value, then jumps in the last month.' },
];

function monthsOf(startYmd: string, endYmd: string): string[] {
  const s = startYmd.slice(0, 7).split('-').map(Number);
  const e = endYmd.slice(0, 7).split('-').map(Number);
  const out: string[] = [];
  let y = s[0]!, m = s[1]!;
  while (y < e[0]! || (y === e[0]! && m <= e[1]!)) { out.push(`${y}-${String(m).padStart(2, '0')}`); m++; if (m > 12) { m = 1; y++; } }
  return out;
}

export default function SeatTrajectory({ plan, seat, canWrite, onClose, onChanged }: {
  plan: PlanVersion; seat: PlanHeadcountRow; canWrite: boolean;
  onClose: () => void; onChanged: () => void;
}) {
  const planId = plan.id;
  const months = monthsOf(plan.horizon_start, plan.horizon_end);
  const schedQ = useQuery(() => api.fpa.plans.headcountSchedule(planId, seat.id), [planId, seat.id]);
  const save = useSaveState();
  const [pending, setPending] = useState<Record<string, number | null>>({});
  const [builder, setBuilder] = useState<ScheduleField | null>(null);

  const editable = canWrite && plan.status === 'draft';
  const rows: ScheduleRow[] = schedQ.data?.schedule ?? [];
  const byKey = new Map(rows.map((r) => [`${r.field}|${r.period_month}`, r]));

  // The seat's own scalar, which every month falls back to. Shown greyed so a
  // scheduled month is visibly different from an inherited one.
  const scalarOf = (f: ScheduleField): number | null => {
    switch (f) {
      case 'utilisation': return seat.target_utilisation;
      case 'bill_rate': return seat.target_bill_rate;
      case 'hours': return seat.target_hours_per_head;
      case 'salary': return seat.annual_base_salary;
      // Productivity has no scalar: it comes from the shared ramp curve, whose
      // value depends on months-since-start and is not resolved client-side.
      case 'productivity': return null;
    }
  };

  const fmt = (f: ScheduleField, v: number | null) => {
    if (v === null || v === undefined) return '';
    const spec = FIELDS.find((x) => x.key === f)!;
    return spec.pct ? `${Math.round(v * 1000) / 10}%` : v.toLocaleString(undefined, { maximumFractionDigits: 0 });
  };

  function setCell(f: ScheduleField, m: string, raw: string) {
    const t = raw.trim();
    if (t === '') { setPending((p) => ({ ...p, [`${f}|${m}`]: null })); return; }
    const spec = FIELDS.find((x) => x.key === f)!;
    const n = Number(t.replace('%', ''));
    if (!Number.isFinite(n)) return;
    // Percent fields are typed as whole numbers and stored as fractions, which
    // is how the rest of the FP&A UI behaves.
    setPending((p) => ({ ...p, [`${f}|${m}`]: spec.pct ? n / 100 : n }));
    save.clear();
  }

  const dirty = Object.keys(pending).length > 0;

  async function saveCells() {
    const values = Object.entries(pending).map(([k, value]) => {
      const [field, period_month] = k.split('|') as [ScheduleField, string];
      return { headcount_id: seat.id, field, period_month, value };
    });
    const ok = await save.run(() => api.fpa.plans.headcountScheduleBatch(planId, { values }), `${values.length} month${values.length === 1 ? '' : 's'}`);
    if (!ok) return;
    setPending({});
    schedQ.refetch();
    onChanged();
  }

  async function clearField(f: ScheduleField) {
    if (!window.confirm(`Clear the ${f} trajectory for this seat? Every month reverts to the seat's own number.`)) return;
    const ok = await save.run(() => api.fpa.plans.headcountScheduleBatch(planId, { clear: [{ headcount_id: seat.id, field: f }] }), `${f} cleared`);
    if (!ok) return;
    schedQ.refetch();
    onChanged();
  }

  const cellStyle: React.CSSProperties = { padding: '2px 5px', textAlign: 'right', fontSize: 11, fontVariantNumeric: 'tabular-nums', borderBottom: '0.5px solid var(--color-border)' };
  const input: React.CSSProperties = { width: 56, fontSize: 11, textAlign: 'right', border: '0.5px solid var(--color-border)', borderRadius: 3, padding: '1px 3px', background: 'var(--color-surface)', color: 'var(--color-text)', fontVariantNumeric: 'tabular-nums' };
  const muted: React.CSSProperties = { fontSize: 10, color: 'var(--color-text-muted)' };

  return (
    <Modal
      title={`Trajectory — ${seat.linked_user_name ?? seat.role_label}`}
      subtitle={`${seat.role_label} · ${seat.start_month}${seat.end_month ? ` → ${seat.end_month}` : ''}${seat.is_billable ? '' : ' · not billable'}`}
      onClose={onClose} width={1000}
    >
      <div style={{ padding: 16 }}>
        {schedQ.isLoading ? <Spinner /> : (
          <>
            <p style={{ ...muted, margin: '0 0 10px' }}>
              A value here applies to that month only. A blank cell falls back to the seat&apos;s own
              number (shown greyed), then to the department driver. Type a percentage as a whole
              number — 70 means 70%.
            </p>

            <div style={{ overflowX: 'auto' }}>
              <table style={{ borderCollapse: 'collapse', minWidth: '100%' }}>
                <thead>
                  <tr>
                    <th style={{ ...cellStyle, textAlign: 'left', fontWeight: 600, position: 'sticky', left: 0, background: 'var(--color-surface)' }}>Driver</th>
                    {months.map((m) => <th key={m} style={{ ...cellStyle, fontWeight: 600 }}>{m.slice(2)}</th>)}
                    <th style={{ ...cellStyle, fontWeight: 600 }} />
                  </tr>
                </thead>
                <tbody>
                  {FIELDS.map((f) => {
                    const scalar = scalarOf(f.key);
                    const hasAny = rows.some((r) => r.field === f.key);
                    return (
                      <tr key={f.key}>
                        <td style={{ ...cellStyle, textAlign: 'left', position: 'sticky', left: 0, background: 'var(--color-surface)' }} title={f.hint}>
                          {f.label}
                          <span style={{ ...muted, marginLeft: 6 }}>
                            {scalar !== null ? fmt(f.key, scalar) : f.key === 'productivity' ? (seat.ramp_curve_id ?? 'no ramp') : ''}
                          </span>
                        </td>
                        {months.map((m) => {
                          const key = `${f.key}|${m}`;
                          const row = byKey.get(key);
                          const buffered = key in pending;
                          const shown = buffered
                            ? (pending[key] === null ? '' : fmt(f.key, pending[key]!))
                            : row ? fmt(f.key, row.value) : '';
                          const inherited = !row && !buffered;
                          return (
                            <td key={m} style={{
                              ...cellStyle,
                              background: buffered ? 'rgba(240,180,0,0.14)' : row?.source === 'manual' ? 'rgba(120,120,255,0.10)' : undefined,
                            }} title={row ? `${row.source}${row.reason ? ` — ${row.reason}` : ''}` : 'inherited from the seat'}>
                              {editable
                                ? <input style={{ ...input, color: inherited ? 'var(--color-text-muted)' : undefined }}
                                    defaultValue={shown}
                                    placeholder={inherited && scalar !== null ? fmt(f.key, scalar) : ''}
                                    onBlur={(e) => setCell(f.key, m, e.target.value)} />
                                : <span style={{ color: inherited ? 'var(--color-text-muted)' : undefined }}>{shown || (scalar !== null ? fmt(f.key, scalar) : '')}</span>}
                            </td>
                          );
                        })}
                        <td style={{ ...cellStyle, whiteSpace: 'nowrap' }}>
                          {editable && <ActionButton onClick={() => setBuilder(f.key)}>Ramp…</ActionButton>}
                          {editable && hasAny && <span style={{ marginLeft: 4 }}><ActionButton onClick={() => clearField(f.key)}>Clear</ActionButton></span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 12 }}>
              <SaveStatus state={save} />
              {dirty && <span style={{ fontSize: 11, color: 'var(--color-amber)' }}>{Object.keys(pending).length} unsaved</span>}
              <span style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                {editable && <ActionButton variant="primary" onClick={saveCells} disabled={save.busy || !dirty}>{save.busy ? 'Saving…' : 'Save & recompute'}</ActionButton>}
                <ActionButton onClick={onClose}>Close</ActionButton>
              </span>
            </div>

            <p style={{ ...muted, marginTop: 8 }}>
              Blue = typed by hand, and a ramp will not overwrite it. Amber = unsaved.
            </p>
          </>
        )}
      </div>

      {builder && (
        <RampBuilder
          planId={planId} seatId={seat.id} field={builder} months={months}
          scalar={scalarOf(builder)}
          onClose={() => setBuilder(null)}
          onApplied={(note) => { setBuilder(null); save.markSaved(note); schedQ.refetch(); onChanged(); }}
        />
      )}
    </Modal>
  );
}

// ─── The ramp builder ───────────────────────────────────────────────────────

/**
 * Build one trajectory. Preview runs the real engine twice on the server and
 * subtracts, so the revenue and salary figures it shows are the ones the save
 * will produce — not a second implementation of the capacity formula that
 * could drift from the engine's.
 */
function RampBuilder({ planId, seatId, field, months, scalar, onClose, onApplied }: {
  planId: string; seatId: string; field: ScheduleField; months: string[];
  scalar: number | null; onClose: () => void; onApplied: (note: string) => void;
}) {
  const spec = FIELDS.find((f) => f.key === field)!;
  const asStored = (n: number) => (spec.pct ? n / 100 : n);
  const asTyped = (n: number | null) => (n === null ? '' : String(spec.pct ? Math.round(n * 1000) / 10 : n));

  const [from, setFrom] = useState(months[0] ?? '');
  const [to, setTo] = useState(months[Math.min(5, months.length - 1)] ?? '');
  const [start, setStart] = useState(asTyped(scalar));
  const [end, setEnd] = useState('');
  const [curve, setCurve] = useState<NonNullable<RampSpecInput['curve']>>('linear');
  const [holdAfter, setHoldAfter] = useState(true);
  const [holdBefore, setHoldBefore] = useState(false);
  const [reason, setReason] = useState('');
  const [preview, setPreview] = useState<RampPreview | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const build = (): RampSpecInput & { headcount_id: string } => ({
    headcount_id: seatId, field,
    from_month: from, to_month: to,
    start_value: asStored(Number(start || 0)), end_value: asStored(Number(end || 0)),
    curve, hold_after: holdAfter, hold_before: holdBefore,
    reason: reason.trim() || null,
  });

  async function run(apply: boolean) {
    setBusy(true); setErr(null);
    try {
      if (apply) {
        const out = await api.fpa.plans.headcountScheduleBatch(planId, { ramps: [build()] });
        onApplied(out.notes?.[0] ?? 'trajectory applied');
      } else {
        const r = await api.fpa.plans.rampPreview(planId, build());
        setPreview(r.preview);
      }
    } catch (e) { setErr((e as Error).message); if (!apply) setPreview(null); }
    finally { setBusy(false); }
  }

  const box: React.CSSProperties = { fontSize: 12, padding: '4px 7px', border: '0.5px solid var(--color-border-strong)', borderRadius: 'var(--radius-sm)', background: 'var(--color-surface)', color: 'var(--color-text)' };
  const row: React.CSSProperties = { display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 };
  const lab: React.CSSProperties = { fontSize: 11, color: 'var(--color-text-muted)', width: 104 };
  const muted: React.CSSProperties = { fontSize: 10, color: 'var(--color-text-muted)' };
  const money = (n: number) => n.toLocaleString(undefined, { maximumFractionDigits: 0 });
  const unit = spec.pct ? '%' : '';

  return (
    <Modal title={`Ramp ${spec.label.toLowerCase()}`} subtitle={spec.hint} onClose={onClose} width={620}>
      <div style={{ padding: 18 }}>
        {err && <p style={{ color: 'var(--color-danger, #c0392b)', fontSize: 12 }}>{err}</p>}

        <div style={row}>
          <span style={lab}>Window</span>
          <select value={from} onChange={(e) => setFrom(e.target.value)} style={box}>{months.map((m) => <option key={m} value={m}>{m}</option>)}</select>
          <span style={muted}>to</span>
          <select value={to} onChange={(e) => setTo(e.target.value)} style={box}>{months.map((m) => <option key={m} value={m}>{m}</option>)}</select>
        </div>

        <div style={row}>
          <span style={lab}>From → to</span>
          <input value={start} onChange={(e) => setStart(e.target.value)} placeholder="48" style={{ ...box, width: 80 }} />
          <span style={muted}>{unit} →</span>
          <input value={end} onChange={(e) => setEnd(e.target.value)} placeholder="70" style={{ ...box, width: 80 }} />
          <span style={muted}>{unit}</span>
          {curve === 'hold' && <span style={muted}>(hold uses the second value)</span>}
        </div>

        <div style={row}>
          <span style={lab}>Shape</span>
          <select value={curve} onChange={(e) => setCurve(e.target.value as NonNullable<RampSpecInput['curve']>)} style={{ ...box, flex: 1 }}>
            {CURVES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
        </div>
        <p style={{ ...muted, margin: '-6px 0 10px 112px' }}>{CURVES.find((c) => c.key === curve)!.hint}</p>

        <div style={row}>
          <span style={lab}>Outside it</span>
          <label style={{ fontSize: 11, display: 'flex', gap: 4, alignItems: 'center' }}>
            <input type="checkbox" checked={holdAfter} onChange={(e) => setHoldAfter(e.target.checked)} />
            hold the end value afterwards
          </label>
          <label style={{ fontSize: 11, display: 'flex', gap: 4, alignItems: 'center' }}>
            <input type="checkbox" checked={holdBefore} onChange={(e) => setHoldBefore(e.target.checked)} />
            pin the start value before
          </label>
        </div>
        <p style={{ ...muted, margin: '-6px 0 10px 112px' }}>
          Without the first, the months after the window fall straight back to the seat&apos;s own number.
        </p>

        <div style={row}>
          <span style={lab}>Reason</span>
          <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. productivity recovery plan, phased return from leave" style={{ ...box, flex: 1 }} />
        </div>

        <div style={row}>
          <span style={lab} />
          <ActionButton onClick={() => run(false)} disabled={busy}>{busy ? 'Working…' : 'Preview'}</ActionButton>
          <ActionButton variant="primary" onClick={() => run(true)} disabled={busy}>Apply &amp; recompute</ActionButton>
        </div>

        {preview && (
          <div style={{ fontSize: 11, marginTop: 4 }}>
            <p style={{ ...muted, margin: '0 0 6px' }}>{preview.note}</p>
            {preview.preserved_manual.length > 0 && (
              <p style={{ color: 'var(--color-amber)', margin: '0 0 6px' }}>
                Leaves {preview.preserved_manual.join(', ')} alone — typed by hand.
              </p>
            )}
            <div style={{ display: 'flex', gap: 16, marginBottom: 6 }}>
              <span>T&amp;M revenue <strong>{preview.totals.rev_tm >= 0 ? '+' : ''}{money(preview.totals.rev_tm)}</strong></span>
              {preview.totals.cost_salary !== 0 && <span>Salary cost <strong>{preview.totals.cost_salary >= 0 ? '+' : ''}{money(preview.totals.cost_salary)}</strong></span>}
              <span style={muted}>over the horizon, for this department</span>
            </div>
            <table style={{ borderCollapse: 'collapse' }}>
              <tbody>
                <tr>
                  <td style={{ ...muted, paddingRight: 8 }}>Month</td>
                  {preview.generated.map((g) => <td key={g.period_month} style={{ ...muted, padding: '0 5px', textAlign: 'right' }}>{g.period_month.slice(2)}</td>)}
                </tr>
                <tr>
                  <td style={{ ...muted, paddingRight: 8 }}>{spec.label}</td>
                  {preview.generated.map((g) => (
                    <td key={g.period_month} style={{ padding: '0 5px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      {spec.pct ? `${Math.round(g.value * 1000) / 10}` : money(g.value)}
                    </td>
                  ))}
                </tr>
                <tr>
                  <td style={{ ...muted, paddingRight: 8 }}>Δ revenue</td>
                  {preview.generated.map((g) => {
                    const e = preview.effect.find((x) => x.period_month === g.period_month);
                    return (
                      <td key={g.period_month} style={{ padding: '0 5px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: (e?.rev_tm ?? 0) < 0 ? 'var(--color-red-text)' : undefined }}>
                        {e ? money(e.rev_tm) : ''}
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Modal>
  );
}
